/**
 * Tracks AG-UI run_id (= trace_id = turn_id) associations for all streaming
 * turns in the current session.
 *
 * Returns a Map<messageId, turnId> so that every streamed assistant message
 * can show turn-level feedback — not just the latest one.
 *
 * The assistant placeholder message is created synchronously by the runtime
 * in append() before the SSE stream starts, so when onRunStartedEvent fires,
 * the message already exists as the last thread message. No timing issues.
 */
import { useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime } from "@assistant-ui/react";

export function useStreamingTurnIds(
  agent: HttpAgent,
  runtime: AssistantRuntime
): Map<string, string> {
  const [turnIdMap, setTurnIdMap] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    const sub = agent.subscribe({
      onRunStartedEvent: ({ event }) => {
        const messages = runtime.thread.getState().messages;
        const lastMsg = messages[messages.length - 1];
        if (lastMsg?.role === "assistant") {
          setTurnIdMap((prev) => {
            const next = new Map(prev);
            next.set(lastMsg.id, event.runId);
            return next;
          });
        }
      },
    });

    return () => sub.unsubscribe();
  }, [agent, runtime]);

  return turnIdMap;
}
